import type { FigmaNodeData } from '@/types/node';
import type { FigmaNodeSnapshot, NodeId, PendingFigmaNodeSnapshotCopy } from 'web';

/** Workflow and FigmaNode IDs encoded in a canonical snapshot storage key. */
export type FigmaNodeSnapshotKeyParts = {
  workflowId: string;
  figmaNodeId: NodeId;
};

const FIGMA_NODE_SNAPSHOT_KEY =
  /^weavy\/figma-node-snapshots\/workflows\/([A-Za-z0-9_-]+)\/([A-Za-z0-9_-]+)\/snapshot\.fig$/;

/** Extracts the workflow and FigmaNode IDs encoded by a canonical snapshot key. */
export function parseFigmaNodeSnapshotKey(snapshotKey: string): FigmaNodeSnapshotKeyParts | undefined {
  const match = FIGMA_NODE_SNAPSHOT_KEY.exec(snapshotKey);
  if (!match) return undefined;

  const workflowId = match[1];
  const figmaNodeId = match[2];
  if (!workflowId || !figmaNodeId) return undefined;
  return { workflowId, figmaNodeId };
}

/**
 * Makes a FigmaNode clone inert until Pixie has copied its snapshot into storage owned by the
 * clone. A noncanonical key remains inert so it cannot abort the surrounding clone transaction;
 * reconciliation reports the copy error. This runs before the clone enters the TS or WASM graph.
 */
export function prepareFigmaNodeDataForDuplicate(data: FigmaNodeData): FigmaNodeData {
  const pendingSnapshotCopy = data.pendingSnapshotCopy ?? pendingCopyFromSnapshot(data.snapshot);
  return {
    ...data,
    snapshot: undefined,
    connectedFigmaFrame: undefined,
    pendingSnapshotCopy,
  };
}

/** Replaces only the copied snapshot's storage descriptor, retaining its origin and output data. */
export function copiedFigmaNodeSnapshot(
  pendingCopy: PendingFigmaNodeSnapshotCopy,
  snapshotStorage: Pick<FigmaNodeSnapshot, 'snapshotKey' | 'snapshotVersionId'>,
): FigmaNodeSnapshot {
  return {
    ...pendingCopy.sourceSnapshot,
    snapshotKey: snapshotStorage.snapshotKey,
    snapshotVersionId: snapshotStorage.snapshotVersionId,
  };
}

function pendingCopyFromSnapshot(snapshot: FigmaNodeSnapshot | undefined): PendingFigmaNodeSnapshotCopy | undefined {
  if (!snapshot) return undefined;
  return { sourceSnapshot: snapshot };
}
